import { TableFooter } from "@material-ui/core";
import { TableHead } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePagination from "@saleor/components/TablePagination";
import { CouponRedemptionListParent_coupon_couponRedemptions_edges_node } from "@saleor/coupons/types/CouponRedemptionListParent";
import { CouponRedemptionListUrlSortField } from "@saleor/coupons/url";
import { SubmitPromise } from "@saleor/hooks/useForm";
import { renderCollection } from "@saleor/misc";
import { ListProps, SortPage } from "@saleor/types";
import { getArrowDirection } from "@saleor/utils/sort";
import React from "react";
import { FormattedMessage } from "react-intl";

const useStyles = makeStyles(
  {
    colRow: {
      fontSize: "1.3em",
      fontWeight: 600,
      whiteSpace: "nowrap",
      width: "auto"
    }
  },
  { name: "CouponRedemptionList" }
);

interface CouponRedemptionListProps
  extends ListProps,
    SortPage<CouponRedemptionListUrlSortField> {
  couponRedemptions?: CouponRedemptionListParent_coupon_couponRedemptions_edges_node[];
  onSubmit?: (data) => SubmitPromise;
}

const CouponRedemptionList: React.FC<CouponRedemptionListProps> = props => {
  const {
    couponRedemptions,
    disabled,
    settings,
    pageInfo,
    sort,
    onNextPage,
    onPreviousPage,
    onUpdateListSettings,
    onSort
  } = props;

  const NUMBER_OF_COLUMNS = 5;
  const classes = useStyles({});

  const availableQuantities = couponRedemptions?.map(
    couponRedemption => couponRedemption?.coupon?.availableQuantity
  );

  const couponRedemptionQuantity = availableQuantities.find(
    quantity => quantity !== null
  );

  const formatDate = dateString => {
    const date = new Date(dateString);
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date
      .getDate()
      .toString()
      .padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  return (
    <ResponsiveTable>
      <TableHead>
        <TableCellHeader className={classes.colRow}>
          <FormattedMessage defaultMessage="Coupon Code" />
        </TableCellHeader>
        <TableCellHeader className={classes.colRow}>
          <FormattedMessage defaultMessage="Coupon Status" />
        </TableCellHeader>
        <TableCellHeader className={classes.colRow}>
          <FormattedMessage defaultMessage="Customer Email" />
        </TableCellHeader>
        <TableCellHeader
          className={classes.colRow}
          direction={
            sort.sort === CouponRedemptionListUrlSortField.claimedAt
              ? getArrowDirection(sort.asc)
              : "asc"
          }
          arrowPosition="right"
          onClick={() => onSort(CouponRedemptionListUrlSortField.claimedAt)}
        >
          <FormattedMessage defaultMessage="Date Claimed" />
        </TableCellHeader>
        <TableCellHeader
          className={classes.colRow}
          direction={
            sort.sort === CouponRedemptionListUrlSortField.redeemedAt
              ? getArrowDirection(sort.asc)
              : "asc"
          }
          arrowPosition="right"
          onClick={() => onSort(CouponRedemptionListUrlSortField.redeemedAt)}
        >
          <FormattedMessage defaultMessage="Date Redeemed" />
        </TableCellHeader>
      </TableHead>
      <TableFooter>
        <TablePagination
          colSpan={NUMBER_OF_COLUMNS}
          settings={settings}
          hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
          onNextPage={onNextPage}
          onUpdateListSettings={onUpdateListSettings}
          hasPreviousPage={
            pageInfo && !disabled ? pageInfo.hasPreviousPage : false
          }
          onPreviousPage={onPreviousPage}
          textInputValue="Coupons Remaining"
          dataInput={couponRedemptionQuantity}
        />
      </TableFooter>
      <TableBody>
        {renderCollection(couponRedemptions, (couponRedemption, index) => {
          const status =
            couponRedemption?.status === "EXPIRED"
              ? "CLAIMED"
              : couponRedemption?.status;
          return (
            <>
              <TableRow
                hover={!!couponRedemption}
                key={couponRedemption ? couponRedemption.id : "skeleton"}
              >
                <TableCell
                  data-test="couponRedemptionCode"
                  id={`couponRedemptionCode${index}`}
                >
                  <div>
                    {couponRedemption && couponRedemption.code ? (
                      <>
                        {couponRedemption.coupon.code}-
                        <strong>{couponRedemption.code}</strong>
                      </>
                    ) : (
                      <Skeleton />
                    )}
                  </div>
                </TableCell>
                <TableCell data-test="couponStatus" id={`couponStatus${index}`}>
                  <div>{couponRedemption ? status : <Skeleton />}</div>
                </TableCell>
                <TableCell
                  data-test="customerEmail"
                  id={`customerEmail${index}`}
                >
                  <div>
                    {couponRedemption && couponRedemption.user.email ? (
                      couponRedemption.user.email
                    ) : (
                      <Skeleton />
                    )}
                  </div>
                </TableCell>
                <TableCell data-test="dateClaimed" id={`dateClaimed${index}`}>
                  <div>
                    {couponRedemption && couponRedemption.claimedAt ? (
                      formatDate(couponRedemption.claimedAt)
                    ) : (
                      <Skeleton />
                    )}
                  </div>
                </TableCell>
                <TableCell data-test="dateRedeemed" id={`dateRedeemed${index}`}>
                  <div>
                    {couponRedemption && couponRedemption.redeemedAt ? (
                      formatDate(couponRedemption.redeemedAt)
                    ) : (
                      <Skeleton />
                    )}
                  </div>
                </TableCell>
              </TableRow>
              <TableRow></TableRow>
            </>
          );
        })}
        {!couponRedemptions || couponRedemptions.length === 0 ? (
          <TableRow>
            <TableCell colSpan={NUMBER_OF_COLUMNS}>
              <FormattedMessage defaultMessage="No coupons found" />
            </TableCell>
          </TableRow>
        ) : null}
      </TableBody>
    </ResponsiveTable>
  );
};

CouponRedemptionList.displayName = "CouponRedemptionList";
export default CouponRedemptionList;
